import { memo, ReactNode, useMemo, useState } from 'react'

import FlyoutWithMobileSlideUp from 'components/FlyoutWithMobileSlideUp'
import { IOptionsType } from 'hooks/useRelativeCoords'
import { ScrollableElementProvider } from 'containers/SubSidebar/AIVideoContent/context'
import { T } from 'consts/translationKeys'
import { translate } from 'utils/translations'

import * as Styled from './styled'

interface IProps {
  isOpen: boolean
  anchorEl: HTMLElement | null
  onClose: () => void
  hasActiveFilters: boolean
  onClearAll: () => void
  children: ReactNode
}

const FLYOUT_OPTIONS: IOptionsType = {
  position: 'bottom',
  alignmentX: 'left',
  extraOffset: 8
}

// The inner filter dropdowns portal their option menus to <body> (outside the
// flyout DOM), so a plain click-outside would treat picking an option as an
// outside click and close the whole flyout. Every dropdown overlay carries the
// `dropdown-overlay-wrapper` class (see common-components/Dropdown) — ignore it.
const IGNORED_SELECTORS = ['dropdown-overlay-wrapper']

const FiltersFlyout = ({
  isOpen,
  anchorEl,
  onClose,
  hasActiveFilters,
  onClearAll,
  children
}: IProps) => {
  // The dropdowns inside FiltersSection call useScrollableElement() to close on
  // scroll — provide the flyout's own scrollable body as that element (the
  // flyout lives outside the tab's ScrollableElementProvider).
  const [bodyEl, setBodyEl] = useState<HTMLDivElement | null>(null)
  const scrollableElementRef = useMemo(() => ({ current: bodyEl }), [bodyEl])

  return (
    <FlyoutWithMobileSlideUp
      isOpen={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      options={FLYOUT_OPTIONS}
      ignoredSelectors={IGNORED_SELECTORS}
    >
      <Styled.FlyoutCard>
        <ScrollableElementProvider scrollableElementRef={scrollableElementRef}>
          <Styled.FlyoutBody ref={setBodyEl}>{children}</Styled.FlyoutBody>
        </ScrollableElementProvider>
        {hasActiveFilters ? (
          <Styled.FlyoutFooter>
            <Styled.ClearAllButton onClick={onClearAll}>
              {translate(T.RESET_LABEL)}
            </Styled.ClearAllButton>
          </Styled.FlyoutFooter>
        ) : null}
      </Styled.FlyoutCard>
    </FlyoutWithMobileSlideUp>
  )
}

export default memo(FiltersFlyout)
